export type SatelliteResponse = {
	status: number;
	hostname: string;
	endpoint: string;
	date: Date;
	data: object;
};

/* Property type */
export type Property = {
	_id: string;
	hostname: string;
	name: string;
	categories: Category[];
	populars?: ArticleThumbnail[];
} & LocalPropertySettings;

export type LocalPropertySettings = {
	aspectRatio: string;
	colors: {
		text: string;
		hover: string;
		background: string;
		hoverBackground: string;
		ring: string;
		safelist: {
			pattern: RegExp;
			variants: string[];
		};
	};
	googleNews?: string;
	appleNews?: string;
	analytics: string;
	cmpDC: string;
	cmpFQ: string;
	authors: Author[];
	subtitle: string;
	description: string;
	bio: string;
	adPlugg?: {
		live: boolean;
		code: string;
		zone: string;
		height: string;
	};
	primisId?: string;
	adsenseId?: string;
	enablePrint?: boolean;
	enablePinterest?: boolean;
	keywords: string[];
	locale: string;
	language: PropertyLanguage;
	fbApp: string;
	fbLike: string;
	enableFbLike: boolean;
	fbPages: string[];
	fbPixel: string;
	links: {
		link: string;
		name: string;
	}[];
	newsletter?: {
		enabled: boolean;
		title: string;
		subtitle: string;
		listId: string;
		golootId: string;
	};
};

export enum PropertyLanguage {
	FR = 'fr',
	EN = 'en'
}

export type Category = {
	name: string;
	slug: string;
	showcase?: boolean;
	hidden?: boolean;
};

export enum VideoProvider {
	PRIMIS = 'primis',
	STN = 'stn'
}

/* Article type */

export type Article = {
	_id: string;
	_propertyId: string;
	slug: string;
	category: Category;
	title: string;
	description: string;
	keywords: string[];
	author: Author;
	acd: string;
	content: ContentBlock[] | string;
	readingTime: number;
	created: Date;
	published: Date;
	modified?: Date;
	metadata?: { [key: string]: string | number | boolean };
	source: {
		url: string;
		text: string;
	};
	credit: {
		url: string;
		text: string;
	};
	imageFeaturedBlurHash: string;
	imageFeatured: Image[];
	imageSocial: Image;
	version: number;
	anonymous: boolean;
};

export type ArticleThumbnail = Pick<
	Article,
	| '_id'
	| '_propertyId'
	| 'category'
	| 'slug'
	| 'title'
	| 'description'
	| 'published'
	| 'modified'
	| 'imageFeatured'
	| 'imageFeaturedBlurHash'
	| 'readingTime'
	| 'anonymous'
> & { author: AuthorThumbnail };

export type Image = {
	url: string;
	size: number[];
	preset?: string;
};

export enum AspectRatio {
	_1BY1 = '1by1',
	_4BY3 = '4by3',
	_16BY9 = '16by9',
	_5BY2 = '5by2',
	_2BY1 = '2by1'
}

export enum LoadingPriority {
	LAZY = 'lazy',
	EAGER = 'eager'
}

export type ContentBlock = {
	type: string;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	data?: { [key: string]: any };
	ordered?: boolean;
};

/* Author */

export type Author = {
	_id: string;
	name: string;
	bio: string;
	title: string;
	slug: string;
	links: {
		name: string;
		url: string;
	}[];
	gravatarId: string;
};

export type AuthorThumbnail = Pick<Author, '_id' | 'name' | 'title' | 'slug' | 'gravatarId'>;

export type SearchResults = {
	query: string;
	page: number;
	pages: number;
	found: number;
	results: ArticleThumbnail[];
};

/* Sitemap */

export type SitemapSummary = {
	category: string;
	pages: number;
	count: number;
	limit: number;
};

export type SitemapEntry = {
	url: string;
	title: string;
	published: string;
	modified: string;
};

/* RSS */

export type RSSFeed = {
	property: Pick<Property, 'name' | 'description' | 'language' | 'categories'>;
	articles: Article[];
};
